// pages/Dashboard/FormLinks.jsx - Form links management page
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dashboardService from '../../services/dashboardService';
import { LinkTable } from '../../components/Dashboard';
import toast from 'react-hot-toast';

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'expired', label: 'Expired' },
  { value: 'revoked', label: 'Revoked' }
];

export default function FormLinks() {
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchLinks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await dashboardService.getLinks({
        page: pagination.page,
        limit: pagination.limit,
        status: status || undefined,
        search: debouncedSearch || undefined,
        sortBy,
        sortOrder
      });

      setLinks(response.links || []);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.totalPages || 0
      }));
    } catch (err) {
      console.error('Failed to fetch links:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, status, debouncedSearch, sortBy, sortOrder]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleViewLink = (shortCode) => {
    navigate(`/dashboard/links/${shortCode}`);
  };

  const handleRevokeLink = async (shortCode) => {
    if (!window.confirm('Are you sure you want to revoke this link? This action cannot be undone.')) {
      return;
    }

    try {
      await dashboardService.revokeLink(shortCode);
      toast.success('Link revoked successfully');
      fetchLinks();
    } catch (err) {
      console.error('Failed to revoke link:', err);
      toast.error(err.message || 'Failed to revoke link');
    }
  };

  const handleResendPDF = async (shortCode) => {
    try {
      toast.loading('Resending PDF...', { id: 'resend-pdf' });
      await dashboardService.resendPDF(shortCode);
      toast.success('PDF resent successfully', { id: 'resend-pdf' });
    } catch (err) {
      console.error('Failed to resend PDF:', err);
      toast.error(err.message || 'Failed to resend PDF', { id: 'resend-pdf' });
    }
  };

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchChange = (newSearch) => {
    setSearch(newSearch);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading form links</h3>
            <p className="mt-2 text-sm text-red-700">{error}</p>
            <button
              onClick={fetchLinks}
              className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Form Links</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage safety assessment form links sent to job sites
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <label htmlFor="search" className="sr-only">
              Search
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                id="search"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search by job number or client name..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Status filter */}
          <div className="sm:w-48">
            <label htmlFor="status" className="sr-only">
              Status
            </label>
            <select
              id="status"
              value={status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Sort options */}
          <div className="sm:w-48">
            <label htmlFor="sort" className="sr-only">
              Sort by
            </label>
            <select
              id="sort"
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [newSortBy, newSortOrder] = e.target.value.split('-');
                setSortBy(newSortBy);
                setSortOrder(newSortOrder);
              }}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md"
            >
              <option value="created_at-desc">Newest first</option>
              <option value="created_at-asc">Oldest first</option>
              <option value="job_number-asc">Job # (A-Z)</option>
              <option value="job_number-desc">Job # (Z-A)</option>
              <option value="client_name-asc">Client (A-Z)</option>
              <option value="client_name-desc">Client (Z-A)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Links table */}
      <LinkTable
        data={links}
        loading={loading}
        onViewLink={handleViewLink}
        onRevokeLink={handleRevokeLink}
        onResendPDF={handleResendPDF}
        pagination={pagination}
        onPageChange={handlePageChange}
      />

      {/* Summary */}
      {!loading && links.length > 0 && (
        <div className="text-sm text-gray-500 text-center">
          Showing {links.length} of {pagination.total} form links
        </div>
      )}
    </div>
  );
}
